import React, { FC, useCallback, useState } from 'react'
import {
  FormikConfig,
  FormikContext,
  FormikHelpers,
  FormikValues,
  useFormik,
} from 'formik'
import { captureException } from '@sentry/react'

import { ErrorBoundary } from '../ErrorBoundary'
import { Stack } from '../Stack'
import { LinkText, Text } from '../Text'
import { ExternalLink } from '../Link'

import { FormErrorContext } from './context'

interface FormProps {
  initialValues: FormikValues
}

const FormError = () => {
  return (
    <Stack>
      <Text variant='errorBox'>
        Something went wrong, please email{' '}
        <span className='notranslate'>
          <ExternalLink to='mailto:hi@guild.host'>
            <LinkText>hi@guild.host</LinkText>
          </ExternalLink>
        </span>{' '}
        for help.
      </Text>
    </Stack>
  )
}

export const Form = ({
  children,
  initialValues,
  validationSchema,
  onSubmit,
  ...formikProps
}: FormProps & FormikConfig<any> & { children: React.ReactNode }) => {
  const [isSubmissionError, setIsSubmissionError] = useState(false)
  const formOnSubmit = useCallback(
    async (values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => {
      try {
        await onSubmit(values, formikHelpers)
        setIsSubmissionError(false)
      } catch (error) {
        setIsSubmissionError(true)
        captureException(error)
      }
    },
    [onSubmit]
  )

  const formik = useFormik({
    ...formikProps,
    initialValues,
    validationSchema,
    onSubmit: formOnSubmit,
  })

  return (
    <ErrorBoundary fallback={<FormError />}>
      <FormikContext.Provider value={formik}>
        <FormErrorContext.Provider value={isSubmissionError}>
          {isSubmissionError && <FormError />}
          {children}
        </FormErrorContext.Provider>
      </FormikContext.Provider>
    </ErrorBoundary>
  )
}
